import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getTravelList,
  addTravelToBusiness,
  updateTravel,
  deleteTravel,
} from "../../../../../../apis/Business/TravelApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getHotelData,
  getTravelCommuteData,
  getHotelLocationDropdown,
} from "../../../../../../apis/Dropdown";

const TravelTable = ({
  businessId,
  subcategory,
  subcategory_description,
  subcategoryId,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownMeans, setDropdownMeans] = useState([]);
  const [dropdownCountries, setDropdownCountries] = useState([]);
  const [dropdownHotel, setDropdownHotel] = useState([]);
  const [isOptional, setIsOptional] = useState(true);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const applyDisableRules = useCallback((item) => {
    const shouldDisable = item["hotel_location.value"] && item.hotel_stays;
    return {
      ...item,
      identical_return: shouldDisable ? false : item.identical_return,
      "means.value": shouldDisable ? null : item["means.value"],
      transport_cost: shouldDisable ? null : item.transport_cost,
    };
  }, []);

  const dynamicPlaceholders = useMemo(
    () => ({
      "hotel_location.value": {
        targetField: "price_invoice",
        getPlaceholder: (selectedValue, dropdownData) => {
          if (selectedValue) {
            setIsOptional(false);
          } else if (selectedValue === null || selectedValue === undefined) {
            setIsOptional(true);
          }
        },
      },
    }),
    []
  );

  const fetchData = useCallback(() => {
    getTravelCommuteData({ setDropdown: setDropdownMeans });
    getHotelLocationDropdown({ setDropdown: setDropdownHotel });
    getHotelData({ setDropdown: setDropdownCountries });
    getTravelList({
      businessId: businessId,
      setObjects: (fetchedData) => {
        const updatedData = fetchedData.map(applyDisableRules);
        setData(updatedData);
      },
      subcategory: subcategory,
      subcategory_description: subcategory_description,
      subcategoryId: subcategoryId,
    });
  }, [
    businessId,
    subcategory,
    subcategoryId,
    subcategory_description,
    applyDisableRules,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      const updatedItem = applyDisableRules(editedItem);
      updateTravel({
        businessId,
        objectId: updatedItem.id,
        ...updatedItem,
        subcategory: subcategory,
        subcategory_description: subcategory_description,
        visiting_country: updatedItem["visiting_country.value"]
          ? updatedItem["visiting_country.value"].value
          : updatedItem["visiting_country"]
          ? updatedItem["visiting_country"].value
          : null,
        means: updatedItem["means.value"]
          ? updatedItem["means.value"].value
          : updatedItem["means"]
          ? updatedItem["means"].value
          : null,
        hotel_location: updatedItem["hotel_location.value"]
          ? updatedItem["hotel_location.value"].value
          : updatedItem["hotel_location"]
          ? updatedItem["hotel_location"].value
          : null,
        subcategoryId: subcategoryId,
        callback: fetchData,
        use_swal: true,
      });
    },
    [
      businessId,
      fetchData,
      subcategory,
      subcategory_description,
      subcategoryId,
      applyDisableRules,
    ]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const updatedItem = applyDisableRules(newItem);
      addTravelToBusiness({
        businessId,
        ...updatedItem,
        visiting_country: updatedItem["visiting_country.value"]
          ? updatedItem["visiting_country.value"].value
          : null,
        hotel_location: updatedItem["hotel_location.value"]
          ? updatedItem["hotel_location.value"].value
          : null,
        means: updatedItem["means.value"]
          ? updatedItem["means.value"].value
          : null,
        subcategory: subcategory,
        subcategory_description: subcategory_description,
        subcategoryId: subcategoryId,
        callback: fetchData,
        use_swal: true,
      });
    },
    [
      businessId,
      fetchData,
      subcategory,
      subcategory_description,
      subcategoryId,
      applyDisableRules,
    ]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteTravel({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "visiting_country.value",
        header: `${t("VISITING_COUNTRY")} *`,
        type: "dropdown",
        dropdownData: dropdownCountries,
        disableSortBy: true,
        width: "110px",
      },
      {
        accessor: "hotel_location.value",
        header: t("Hotel stays"),
        type: "dropdown",
        dropdownData: dropdownHotel,
        disableSortBy: true,
        width: "110px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIP_HOTEL_STAYS"),
      },
      {
        accessor: "hotel_stays",
        header: t("No of days"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: true,
      },
      {
        accessor: "means.value",
        header: t("MEANS"),
        type: "dropdown",
        dropdownData: dropdownMeans,
        disableSortBy: true,
        width: "110px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIP_TRAVEL_DISTANCE"),
      },
      {
        accessor: "transport_cost",
        header: `${t("Distance travelled")} *`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: true,
      },
      {
        accessor: "identical_return",
        header: t("Identical Return"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("IDENTICAL_RETURN_TIP"),
      },

      {
        accessor: "no_of_employees",
        header: `${t("NO_EMPLOYEES")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: `${t("PRICE_INVOICE")} ${isOptional ? "" : "*"}`,
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: isOptional,
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [dropdownCountries, dropdownHotel, dropdownMeans, isOptional, t]
  );

  const disableRules = {
    carbon: () => true,
    "means.value": (rowData, row) =>
      (rowData["hotel_location.value"] && rowData.hotel_stays) ||
      (row && row.original.isDisabled),
    identical_return: (rowData, row) =>
      (rowData["hotel_location.value"] && rowData.hotel_stays) ||
      (row && row.original.isDisabled),
    transport_cost: (rowData, row) =>
      (rowData["hotel_location.value"] && rowData.hotel_stays) ||
      (row && row.original.isDisabled),
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "visiting_country.value": dropdownCountries,
      "means.value": dropdownMeans,
      "hotel_location.value": dropdownHotel,
    }),
    [dropdownCountries, dropdownHotel, dropdownMeans]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={(newData) => setData(newData.map(applyDisableRules))}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default TravelTable;
